import { categories, sites } from "@/mocks/nav";
import type { NavCategory, NavSite, SiteWithCategory } from "@/types/nav";

const categoryMap = new Map<string, NavCategory>(
  categories.map((c) => [c.name, c] as [string, NavCategory]),
);

export function getCategories(): NavCategory[] {
  return categories;
}

export function getCategoryByName(id: string): NavCategory | undefined {
  return categoryMap.get(id);
}

export function withCategory(site: NavSite): SiteWithCategory {
  const category = categoryMap.get(site.categoryId);
  return {
    ...site,
    categoryName: category?.name ?? site.categoryId,
    categoryIcon: category?.icon ?? "ri-link",
  };
}

export function getAllSites(): SiteWithCategory[] {
  return sites.map(withCategory);
}

export function getSiteById(id: string): SiteWithCategory | undefined {
  const site = sites.find((s) => s.id === id);
  return site ? withCategory(site) : undefined;
}

export function getSitesByCategory(categoryName: string): SiteWithCategory[] {
  return sites.filter((s) => s.categoryId === categoryName).map(withCategory);
}

export function getFeatured(limit = 6): SiteWithCategory[] {
  return sites.filter((s) => s.featured).slice(0, limit).map(withCategory);
}

export function getLatest(limit = 8): SiteWithCategory[] {
  return [...sites]
    .sort((a, b) => (a.addedAt < b.addedAt ? 1 : -1))
    .slice(0, limit)
    .map(withCategory);
}

export function getHot(limit = 10): SiteWithCategory[] {
  return [...sites]
    .sort((a, b) => b.visits - a.visits)
    .slice(0, limit)
    .map(withCategory);
}

export function getRelated(site: NavSite, limit = 5): SiteWithCategory[] {
  const sameCategory = sites.filter(
    (s) => s.categoryId === site.categoryId && s.id !== site.id,
  );
  const ordered = [
    ...sameCategory.filter((s) => s.subCategory === site.subCategory),
    ...sameCategory.filter((s) => s.subCategory !== site.subCategory),
  ];
  if (ordered.length === 0) {
    return getHot(limit);
  }
  return ordered.slice(0, limit).map(withCategory);
}

export function searchSites(keyword: string, categoryName?: string): SiteWithCategory[] {
  const kw = keyword.trim().toLowerCase();
  let pool = sites;
  if (categoryName) {
    pool = pool.filter((s) => s.categoryId === categoryName);
  }
  if (!kw) {
    return pool.map(withCategory);
  }
  return pool
    .filter((s) => {
      const haystack = [s.name, s.description, s.categoryId, s.subCategory, ...s.tags]
        .join(" ")
        .toLowerCase();
      return haystack.includes(kw);
    })
    .map(withCategory);
}

export function formatVisits(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  return String(value);
}

export function formatDate(value: string): string {
  const [y, m, d] = value.split("-");
  return `${y}年${m}月${d}日`;
}